/**
 * useWhiteboardPage: 독립 화이트보드 페이지(/whiteboard)용 상태 관리 훅.
 * v1 원본: pages/WhiteboardPage/useWhiteboardPage.js
 *
 * URL 해시에서 roomId/roomKey 파싱 → 페이지 네비게이션 → 참여자 추적
 */
import { useEffect, useState, useRef, useCallback } from 'react';
import { parseURLParams } from '@/utils/whiteboard/baseUtils';
import { convertUrlToBase64 } from '@/utils/whiteboard/baseUtils';
import { getRoomInfo } from '@/utils/whiteboard';

interface WhiteboardPageState {
  roomId: string;
  roomKey: string;
  localParticipantName: string;
  roomList: Array<{ pageNum: number; collabDetails: { roomId: string; roomKey: string } }>;
  jitsiId: string;
  initialZoom: number;
  conferenceId: string;
}

interface CollabParticipant {
  isOwner?: boolean;
  jitsiId?: string;
  roomIndex?: number;
  canEdit?: boolean;
}

export function useWhiteboardPage({ isViewOnly = false }: { isViewOnly?: boolean }) {
  const search = parseURLParams(window.location.href, true);
  const rawState = search['state'];
  const state: WhiteboardPageState | null = rawState
    ? JSON.parse(decodeURIComponent(rawState as string))
    : null;

  const [currentPageNumber, setCurrentPageNumber] = useState(1);
  const [fileList, setFileList] = useState<Array<{ dataURL: string; thumbnailURL?: string; [k: string]: unknown }>>([]);
  const [pdfFileName, setPdfFileName] = useState('화이트보드');
  const [editPermissions, setEditPermissions] = useState<unknown>(null);
  const [isSessionOwner, setIsSessionOwner] = useState(false);
  const [isViewOnlyState, setIsViewOnlyState] = useState(isViewOnly);
  const [participants, setParticipants] = useState<CollabParticipant[]>([]);
  const [isPermissionLoaded, setIsPermissionLoaded] = useState(false);

  const participantsRef = useRef<CollabParticipant[]>([]);

  const fileLoadedRef = useRef(false);
  const localJitsiId = state?.jitsiId;

  const updateEditState = useCallback((
    nextParticipants: CollabParticipant[],
    nextEditPermissions: unknown = editPermissions,
    permissionLoaded = isPermissionLoaded,
  ) => {
    if (!localJitsiId) return;

    const owner = nextParticipants.find((pp) => pp.isOwner);
    const isOwner = owner?.jitsiId === localJitsiId;
    const canEditByParticipant = nextParticipants.some(
      (pp) => pp.jitsiId === localJitsiId && pp.canEdit === true,
    );
    const canEditByPermission =
      permissionLoaded &&
      (nextEditPermissions === null ||
        (Array.isArray(nextEditPermissions) && nextEditPermissions.includes(localJitsiId)));

    if (owner) {
      setIsSessionOwner(isOwner);
    }
    setIsViewOnlyState(!(isOwner || canEditByParticipant || canEditByPermission));
  }, [editPermissions, isPermissionLoaded, localJitsiId]);

  // 네이티브 앱(Android/iOS 웹뷰)에서 메시지 수신
  // v1 원본: handleMessageFromNative — FILE_LIST 이벤트 처리
  useEffect(() => {
    const handleMessageFromNative = async (event: MessageEvent) => {
      if (event.data && typeof event.data === 'string') {
        try {
          const data = JSON.parse(event.data);
          console.log('Parsed data:', data);

          if (data.type === 'FILE_LIST') {
            // 이미 파일 로드 완료된 경우 중복 처리 방지
            if (fileLoadedRef.current) {
              console.log('[handleMessageFromNative] FILE_LIST 이미 처리됨, 스킵');
              return;
            }
            const files = data.files as Array<{ thumbnailURL: string; [k: string]: unknown }>;
            const loadedFiles = await Promise.all(
              files.map(async (v) => {
                // v1 원본: dev 환경 URL 치환 후 base64 변환
                const base64DataURL = await convertUrlToBase64(
                  v.thumbnailURL.replace(
                    'http://dev.api0.wehago.com/',
                    'https://rtctest.wehago.com/api0/',
                  ),
                );
                return { ...v, dataURL: base64DataURL };
              }),
            );
            fileLoadedRef.current = true;
            setFileList(loadedFiles);
            console.log('파일 목록이 업데이트되었습니다. 총 ' + loadedFiles.length + '개의 파일이 있습니다.');
          }
        } catch (e) {
          console.error('Failed to parse native message:', e);
        }
      }
    };

    window.addEventListener('message', handleMessageFromNative);
    document.addEventListener('message', handleMessageFromNative as unknown as EventListener); // Android용

    console.log('Message listeners registered');

    return () => {
      window.removeEventListener('message', handleMessageFromNative);
      document.removeEventListener('message', handleMessageFromNative as unknown as EventListener);
    };
  }, []);

  const onChangeParticipants = useCallback(
    (p: CollabParticipant[]) => {
      if (p && p.length > 0) {
        const ownerAfter = p.find((pp) => pp.isOwner);
        const ownerBefore = participantsRef.current.find((pp) => pp.isOwner);
        // 오너가 페이지를 이동한 경우에만 따라감
        if (
          ownerAfter &&
          ownerBefore &&
          ownerAfter.roomIndex !== ownerBefore.roomIndex &&
          currentPageNumber !== (ownerAfter.roomIndex ?? 0) + 1
        ) {
          setCurrentPageNumber((ownerAfter.roomIndex ?? 0) + 1);
        }
        // 현재 사용자가 오너인지 확인
        if (ownerAfter && localJitsiId) {
          setIsSessionOwner(ownerAfter.jitsiId === localJitsiId);
        }
      }
      participantsRef.current = p;
      setParticipants([...p]);
      updateEditState(p);
    },
    [currentPageNumber, localJitsiId, updateEditState],
  );

  const getWhiteboardInfo = useCallback(
    async (conferenceId: string) => {
      if (!conferenceId) return;
      const result = await getRoomInfo(conferenceId);
      if (!result) return;
      const nextEditPermissions = result.editPermissions !== undefined ? result.editPermissions : editPermissions;
      setEditPermissions(nextEditPermissions);
      setIsPermissionLoaded(true);
      setPdfFileName(result.pdfFileName || '화이트보드');
      if (result.participants && result.participants.length > 0) {
        const nextParticipants = result.participants as CollabParticipant[];
        const owner = result.participants.find((pp) => pp.isOwner);
        if (owner && localJitsiId) {
          setIsSessionOwner(owner.jitsiId === localJitsiId);
        }
        setParticipants(nextParticipants);
        updateEditState(nextParticipants, nextEditPermissions, true);
      } else {
        updateEditState(participantsRef.current, nextEditPermissions, true);
      }
    },
    [editPermissions, localJitsiId, updateEditState],
  );

  // ─── state 없으면 invalid ──────────────────────────────────────────
  if (!state) {
    return { isValidState: false as const };
  }

  const {
    roomId,
    roomKey,
    localParticipantName,
    roomList = [],
    jitsiId,
    initialZoom,
    conferenceId,
  } = state;

  // 현재 페이지의 roomInfo 결정
  let currentRoomInfo: { roomId: string; roomKey: string };
  if (roomList.length > 0) {
    const found = roomList.find(({ pageNum }) => pageNum === currentPageNumber);
    currentRoomInfo = found ? found.collabDetails : { roomId, roomKey };
  } else {
    currentRoomInfo = { roomId, roomKey };
  }

  const totalPages = roomList.length > 0 ? roomList.length : 1;
  const isViewPageInfo = roomList.length > 0; // 문서공유 모드
  const isFileLoading = isViewPageInfo && fileList.length === 0;

  const handlePrevPage = () => {
    if (currentPageNumber > 1) setCurrentPageNumber(currentPageNumber - 1);
  };

  const handleNextPage = () => {
    if (currentPageNumber < totalPages) setCurrentPageNumber(currentPageNumber + 1);
  };

  return {
    isValidState: true as const,
    state,
    currentPageNumber,
    setCurrentPageNumber,
    fileList,
    pdfFileName,
    onChangeParticipants,
    getWhiteboardInfo,
    currentRoomInfo,
    roomId,
    roomKey,
    localParticipantName,
    roomList,
    jitsiId,
    initialZoom,
    conferenceId,
    totalPages,
    handlePrevPage,
    handleNextPage,
    isViewPageInfo,
    isFileLoading,
    isViewOnly: isViewOnlyState,
    editPermissions,
    isSessionOwner,
    participants,
  };
}
